/**
 * Web 前端错误监控与上报
 *
 * 模块说明：
 * 本模块用于 **Web 管理后台前端** 的错误捕获和上报。
 *
 * ⚠️ 注意区分：
 * - 本模块 (error-handler.ts)：Web 前端错误上报
 *   API 路径：POST /api/v1/web-logs/errors
 *   数据存储：mes_web_error_logs 表
 *   查看界面：/system/web-monitoring/errors
 *
 * - App 端错误上报：移动 App 的错误上报（iOS/Android/小程序）
 *   API 路径：POST /api/v1/app-logs/errors
 *   数据存储：mes_app_error_logs 表
 *   查看界面：/system/app-monitoring/errors
 *
 * 功能：
 * 1. 捕获 Vue 组件错误
 * 2. 捕获未处理的 Promise 错误
 * 3. 捕获 JS 运行时错误
 * 4. 捕获资源加载错误
 * 5. 错误去重和限流
 * 6. 离线缓存和重试上报
 *
 * @see api/modules/web-logs.ts - Web 前端日志 API
 * @see modules/mes/web-monitoring/ - Web 日志查看界面
 * @author YSFL Team
 * @date 2026-01-23
 * @updated 2026-01-24 优化 API 路径命名（/web/ -> /web-logs/）
 */
import type { App } from 'vue';

import type {
  BatchUploadWebErrorsForm,
  WebErrorLevel,
  WebErrorLogForm,
  WebErrorType,
} from '#/api/modules/web-logs';

import { useUserStore } from '@vben/stores';

import { webLogsApi } from '#/api/modules/web-logs';
import { getAppBuildVersion } from '#/utils/app-update';
import { getDeviceId, getSessionId } from '#/utils/device';
import { sendLogsBeacon } from '#/utils/log-service-config';
import { updateConfigFromResponse } from '#/utils/monitoring-config';

/**
 * 格式化本地时间为 YYYY-MM-DD HH:mm:ss 格式
 * 避免 ISO 8601 的时区问题
 */
function formatLocalDateTime(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function safePageUrl(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

function pageQueryKeys(): string[] {
  return [...new Set(new URLSearchParams(window.location.search).keys())];
}

/**
 * 获取当前用户信息
 */
function getCurrentUser(): {
  realName: null | string;
  roles: string[];
  userId: null | number;
  username: null | string;
} {
  try {
    const userStore = useUserStore();
    const userInfo = userStore.userInfo;
    return {
      userId: userInfo?.id || null,
      username: userInfo?.username || null,
      realName: userInfo?.realName || null,
      roles: userInfo?.roles || [],
    };
  } catch {
    return { userId: null, username: null, realName: null, roles: [] };
  }
}

/**
 * 获取网络连接类型
 */
function getConnectionType(): string {
  const connection = (navigator as any).connection;
  if (connection) {
    return connection.effectiveType || connection.type || 'unknown';
  }
  return 'unknown';
}

/**
 * 获取网络信息
 */
function getNetworkInfo(): {
  connectionType: string;
  downlink: null | number;
  isOnline: boolean;
  rtt: null | number;
} {
  const connection = (navigator as any).connection;
  return {
    connectionType: getConnectionType(),
    downlink: connection?.downlink ?? null,
    rtt: connection?.rtt ?? null,
    isOnline: navigator.onLine,
  };
}

// 错误上报配置
interface ErrorHandlerOptions {
  /** 是否启用错误上报 */
  enabled?: boolean;
  /** 最大缓存错误数 */
  maxCacheSize?: number;
  /** 相同错误的去重时间窗口（毫秒） */
  dedupeWindow?: number;
  /** 是否在控制台显示错误 */
  showInConsole?: boolean;
  /** 是否显示用户提示 */
  showUserTip?: boolean;
  /** 批量上报阈值 */
  batchSize?: number;
  /** 最大重试次数（防止无限重试） */
  maxRetryCount?: number;
}

// 默认配置
const defaultOptions: Required<ErrorHandlerOptions> = {
  enabled: import.meta.env.PROD, // 生产环境默认启用
  maxCacheSize: 100,
  dedupeWindow: 30_000, // 30秒内相同错误不重复上报（降低 CPU 占用）
  showInConsole: true,
  showUserTip: false, // 默认不显示用户提示，避免打扰
  batchSize: 20, // 积累20条后批量上报（降低 CPU 占用）
  maxRetryCount: 3, // 最多重试3次
};

// 错误指纹缓存（用于去重）
const errorFingerprints = new Map<string, number>();

// 待上报的错误队列（包含重试计数）
interface PendingError {
  error: WebErrorLogForm;
  retryCount: number;
}
const pendingErrors: PendingError[] = [];

// 连续上报失败计数（用于指数退避）
let consecutiveFailures = 0;
const MAX_CONSECUTIVE_FAILURES = 5; // 连续失败5次后暂停上报

// 配置
let options: Required<ErrorHandlerOptions> = { ...defaultOptions };

// 防止重复初始化
let isInitialized = false;

// 缓存设备信息
let cachedDeviceId: null | string = null;
let cachedSessionId: null | string = null;

/**
 * 获取设备ID（带缓存）
 */
async function getDeviceIdCached(): Promise<string> {
  if (!cachedDeviceId) {
    cachedDeviceId = await getDeviceId();
  }
  return cachedDeviceId;
}

/**
 * 获取会话ID（带缓存）
 */
function getSessionIdCached(): string {
  if (!cachedSessionId) {
    cachedSessionId = getSessionId();
  }
  return cachedSessionId;
}

/**
 * 获取浏览器信息
 */
function getBrowserInfo(): { browser: string; version: string } {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let version = '';

  if (ua.includes('Chrome')) {
    browser = 'Chrome';
    version = ua.match(/Chrome\/(\d+)/)?.[1] || '';
  } else if (ua.includes('Firefox')) {
    browser = 'Firefox';
    version = ua.match(/Firefox\/(\d+)/)?.[1] || '';
  } else if (ua.includes('Safari')) {
    browser = 'Safari';
    version = ua.match(/Version\/(\d+)/)?.[1] || '';
  } else if (ua.includes('Edge')) {
    browser = 'Edge';
    version = ua.match(/Edge\/(\d+)/)?.[1] || '';
  }

  return { browser, version };
}

/**
 * 获取操作系统
 */
function getOS(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
}

/**
 * 生成错误指纹（用于去重）
 */
function generateFingerprint(error: WebErrorLogForm): string {
  return `${error.error_type}:${error.error_message}:${error.page_url}`;
}

/**
 * 需要忽略的错误消息模式
 */
const IGNORED_ERROR_PATTERNS = [
  // ResizeObserver 循环错误 - 由浏览器和组件库引起，不影响功能
  'ResizeObserver loop',
  'ResizeObserver loop completed with undelivered notifications',
  // 认证相关错误 - 用户退出登录后不应重复记录
  '401',
  'Unauthorized',
  '403',
  'Forbidden',
  'Token',
  'token',
  '登录已过期',
  '会话已过期',
  '请重新登录',
  // 认证相关错误英文界面匹配串（HTTP 兜底文案 i18n 化后双语言去重均需命中）
  'Session expired',
  'sign in again',
];

/**
 * 检查错误是否应该被忽略（去重）
 */
function shouldIgnore(error: WebErrorLogForm): boolean {
  // 检查是否匹配忽略模式
  const message = error.error_message || '';
  for (const pattern of IGNORED_ERROR_PATTERNS) {
    if (message.includes(pattern)) {
      return true;
    }
  }

  const fingerprint = generateFingerprint(error);
  const lastTime = errorFingerprints.get(fingerprint);

  if (lastTime && Date.now() - lastTime < options.dedupeWindow) {
    return true;
  }

  errorFingerprints.set(fingerprint, Date.now());

  // 清理过期的指纹
  if (errorFingerprints.size > 100) {
    const now = Date.now();
    for (const [key, time] of errorFingerprints.entries()) {
      if (now - time > options.dedupeWindow * 2) {
        errorFingerprints.delete(key);
      }
    }
  }

  return false;
}

/**
 * 转换错误类型
 */
function mapErrorType(
  type: 'api' | 'js' | 'promise' | 'resource' | 'vue',
): WebErrorType {
  const mapping: Record<string, WebErrorType> = {
    api: 'api_error',
    js: 'js_error',
    promise: 'promise_error',
    resource: 'resource_error',
    vue: 'vue_error',
  };
  return mapping[type] || 'js_error';
}

/**
 * 获取错误级别
 */
function getErrorLevel(
  type: 'api' | 'js' | 'promise' | 'resource' | 'vue',
): WebErrorLevel {
  if (type === 'vue') return 'error';
  if (type === 'api') return 'warning';
  return 'error';
}

/**
 * 检查网络是否在线
 */
function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

/**
 * 检查用户是否已登录
 */
function isUserLoggedIn(): boolean {
  try {
    const userStore = useUserStore();
    return !!userStore.userInfo?.id;
  } catch {
    return false;
  }
}

/**
 * 批量上报错误到服务器
 *
 * 防护机制：
 * 1. 用户未登录时跳过上报（避免脏数据）
 * 2. 网络离线时跳过上报
 * 3. 连续失败超过阈值时暂停上报
 * 4. 单条错误重试次数超限后丢弃
 */
async function flushErrors(): Promise<void> {
  if (pendingErrors.length === 0 || !options.enabled) {
    return;
  }

  // 检查用户登录状态（未登录时不上报，避免脏数据）
  if (!isUserLoggedIn()) {
    if (import.meta.env.DEV) {
      console.warn('[ErrorHandler] 用户未登录，跳过上报并清空缓存');
    }
    // 清空缓存，避免退出登录后积累大量无效错误
    pendingErrors.length = 0;
    return;
  }

  // 检查网络状态
  if (!isOnline()) {
    if (import.meta.env.DEV) {
      console.warn('[ErrorHandler] 网络离线，跳过上报');
    }
    return;
  }

  // 检查是否连续失败过多（指数退避）
  if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
    // 每次跳过后减少计数，允许稍后重试
    consecutiveFailures = Math.max(0, consecutiveFailures - 1);
    if (import.meta.env.DEV) {
      console.warn('[ErrorHandler] 连续失败过多，暂停上报，稍后重试');
    }
    return;
  }

  const errorsToReport = pendingErrors.splice(0, options.batchSize);

  try {
    const payload: BatchUploadWebErrorsForm = {
      logs: errorsToReport.map((item) => item.error),
    };
    const response = await webLogsApi.batchUploadErrors(payload);

    // 上报成功，重置连续失败计数
    consecutiveFailures = 0;

    // 处理响应中的监控配置（增量更新，支持完整模式和精简模式）
    if (response?.monitoringConfig) {
      await updateConfigFromResponse(response.monitoringConfig);
    }
  } catch {
    // 上报失败，增加连续失败计数
    consecutiveFailures++;

    // 过滤掉重试次数超限的错误
    const retriableErrors = errorsToReport
      .map((item) => ({
        error: item.error,
        retryCount: item.retryCount + 1,
      }))
      .filter((item) => item.retryCount <= options.maxRetryCount);

    // 记录被丢弃的错误数量
    const discardedCount = errorsToReport.length - retriableErrors.length;
    if (discardedCount > 0 && import.meta.env.DEV) {
      console.warn(`[ErrorHandler] 丢弃 ${discardedCount} 条重试超限的错误`);
    }

    // 放回队列头部（优先重试）
    pendingErrors.unshift(...retriableErrors);

    // 限制队列大小（从尾部截断，保留最新的错误）
    if (pendingErrors.length > options.maxCacheSize) {
      const overflow = pendingErrors.length - options.maxCacheSize;
      pendingErrors.splice(options.maxCacheSize);
      if (import.meta.env.DEV) {
        console.warn(`[ErrorHandler] 队列溢出，丢弃 ${overflow} 条旧错误`);
      }
    }
  }
}

/**
 * 收集错误并添加到队列
 */
async function collectError(
  type: 'api' | 'js' | 'promise' | 'resource' | 'vue',
  message: string,
  extra?: {
    componentName?: string;
    context?: Record<string, any>;
    lifecycleHook?: string;
    route?: string;
    stack?: string;
  },
): Promise<void> {
  const deviceId = await getDeviceIdCached();
  const sessionId = getSessionIdCached();
  const browserInfo = getBrowserInfo();
  const currentUser = getCurrentUser();
  const networkInfo = getNetworkInfo();

  // 构建增强的 context，包含网络信息和用户详细信息
  const enhancedContext: Record<string, any> = {
    // 网络信息（数据库没有 connection_type 字段，放入 context）
    network: {
      connectionType: networkInfo.connectionType,
      downlink: networkInfo.downlink,
      rtt: networkInfo.rtt,
      isOnline: networkInfo.isOnline,
    },
    // 用户详细信息
    user: {
      realName: currentUser.realName,
      roles: currentUser.roles,
    },
    // 时区信息
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    // 构建版本（sourcemap 堆栈还原按此对齐版本）
    buildId: getAppBuildVersion()?.buildId ?? null,
    // 只记录查询参数名，避免把敏感查询值写入错误日志
    queryKeys: pageQueryKeys(),
    // 合并原有的 context
    ...extra?.context,
  };

  const error: WebErrorLogForm = {
    device_id: deviceId,
    session_id: sessionId,
    user_id: currentUser.userId,
    username: currentUser.username || currentUser.realName,
    error_type: mapErrorType(type),
    error_level: getErrorLevel(type),
    error_message: message,
    error_stack: extra?.stack,
    page_url: safePageUrl(),
    page_route: extra?.route || window.location.pathname,
    component_name: extra?.componentName,
    lifecycle_hook: extra?.lifecycleHook,
    browser: browserInfo.browser,
    browser_version: browserInfo.version,
    os: getOS(),
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    user_agent: navigator.userAgent,
    context: enhancedContext,
    // 使用本地时间格式（YYYY-MM-DD HH:mm:ss），后端已配置为 Asia/Shanghai 时区
    occurred_at: formatLocalDateTime(new Date()),
  };

  // 检查是否应该忽略
  if (shouldIgnore(error)) {
    return;
  }

  // 控制台输出
  if (options.showInConsole) {
    console.error(`[${type.toUpperCase()} Error]`, message, error);
  }

  // 添加到队列（初始重试计数为0）
  pendingErrors.push({ error, retryCount: 0 });

  // 达到批量阈值时上报
  if (pendingErrors.length >= options.batchSize) {
    flushErrors();
  }
}

/**
 * 设置 Vue 错误处理器
 */
function setupVueErrorHandler(app: App): void {
  app.config.errorHandler = (err, instance, info) => {
    collectError('vue', err instanceof Error ? err.message : String(err), {
      stack: err instanceof Error ? err.stack : undefined,
      componentName: instance?.$options?.name || 'Unknown',
      lifecycleHook: info,
      route: instance?.$route?.fullPath,
    });
  };

  // Vue 警告处理（仅开发环境）
  if (import.meta.env.DEV) {
    app.config.warnHandler = (msg, instance, trace) => {
      console.warn(`[Vue Warn] ${msg}`, {
        component: instance?.$options?.name,
        trace,
      });
    };
  }
}

/**
 * 设置全局 JS 错误处理器
 */
function setupGlobalErrorHandler(): void {
  window.addEventListener('error', (event) => {
    // 过滤资源加载错误（会被 setupResourceErrorHandler 处理）
    if (event.target && (event.target as HTMLElement).tagName) {
      return;
    }

    collectError('js', event.message, {
      stack: event.error?.stack,
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });
}

/**
 * 设置 Promise 错误处理器
 */
function setupPromiseErrorHandler(): void {
  window.addEventListener('unhandledrejection', (event) => {
    if (event.defaultPrevented) {
      return;
    }

    collectError('promise', event.reason?.message || String(event.reason), {
      stack: event.reason?.stack,
    });
  });
}

/**
 * 设置资源加载错误处理器
 */
function setupResourceErrorHandler(): void {
  window.addEventListener(
    'error',
    (event) => {
      const target = event.target as HTMLElement;
      if (!target || !target.tagName) return;

      const tagName = target.tagName.toLowerCase();
      if (!['audio', 'img', 'link', 'script', 'video'].includes(tagName)) {
        return;
      }

      collectError('resource', `资源加载失败: ${tagName}`, {
        context: {
          tagName,
          src:
            (target as HTMLImageElement).src ||
            (target as HTMLScriptElement).src ||
            (target as HTMLLinkElement).href,
        },
      });
    },
    true,
  ); // 使用捕获模式
}

/**
 * 初始化错误处理器
 */
export function setupErrorHandler(
  app: App,
  customOptions?: ErrorHandlerOptions,
): void {
  // 防止重复初始化（热更新时会多次调用）
  if (isInitialized) {
    // eslint-disable-next-line no-console
    console.info('[ErrorHandler] 已初始化，跳过重复初始化');
    return;
  }

  // 合并配置
  options = { ...defaultOptions, ...customOptions };

  // 标记已初始化
  isInitialized = true;

  // 设置各类错误处理器
  setupVueErrorHandler(app);
  setupGlobalErrorHandler();
  setupPromiseErrorHandler();
  setupResourceErrorHandler();

  // 页面卸载时上报剩余错误
  window.addEventListener('beforeunload', () => {
    if (pendingErrors.length > 0 && options.enabled && isOnline()) {
      // 使用 sendBeacon 确保数据发送（页面卸载时仍能发送）
      // URL 从 log-service-config 获取，支持切换到独立日志服务器
      const logs = pendingErrors.map((item) => item.error);
      sendLogsBeacon('error', logs);
    }
  });

  // 定期上报（每120秒，降低 CPU 占用）
  setInterval(() => {
    flushErrors();
  }, 120_000);

  // eslint-disable-next-line no-console
  console.info('[ErrorHandler] Web端错误监控已初始化', {
    enabled: options.enabled,
    batchSize: options.batchSize,
  });
}

/**
 * 手动上报错误（供业务代码调用）
 */
export function reportCustomError(
  message: string,
  extra?: Record<string, any>,
): void {
  collectError('js', message, { context: extra });
}

/**
 * 上报 API 错误（供请求拦截器调用）
 */
export function reportApiError(
  errorMessage: string,
  extra?: Record<string, any>,
): void {
  collectError('api', errorMessage, { context: extra });
}

/**
 * 清空待上报的错误队列
 *
 * 用于用户退出登录时调用，避免：
 * 1. 退出后积累的脏数据
 * 2. 登录后批量上传大量无效错误
 */
export function clearPendingErrors(): void {
  const count = pendingErrors.length;
  pendingErrors.length = 0;
  errorFingerprints.clear();
  consecutiveFailures = 0;

  if (count > 0) {
    // eslint-disable-next-line no-console
    console.info(`[ErrorHandler] 已清空 ${count} 条待上报错误`);
  }
}

/**
 * 获取待上报错误数量（用于调试）
 */
export function getPendingErrorCount(): number {
  return pendingErrors.length;
}
